import React, { useState, useRef, useMemo } from 'react';
import { Box, Button, TextField, Typography, CircularProgress, Card, CardContent, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { TourFormData } from '../ScheduleTourFlow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { config } from '../../../config/env';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { locationsByState } from '../../../data/schoolLocations';
import SmsConsentCheckbox from '../SmsConsentCheckbox';

interface ContactInfoStepProps {
    formData: TourFormData;
    onNext: () => void;
    onBack: () => void;
    updateFormData: (data: Partial<TourFormData>) => void;
}

function ContactInfoStep({ formData, onNext, onBack, updateFormData }: ContactInfoStepProps) {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const hcaptchaRef = useRef<HCaptcha>(null);
    const [birthdayValue, setBirthdayValue] = useState<Dayjs | null>(
        formData.birthday ? dayjs(formData.birthday) : null
    );

    // Get available grades for the selected school
    const availableGrades = useMemo(() => {
        if (!formData.school) return [];

        const allLocations = Object.values(locationsByState).flat();
        const selectedSchool = allLocations.find(loc => loc.schoolKey === formData.school);
        return selectedSchool?.grades || [];
    }, [formData.school]);

    const handleCaptchaVerify = (token: string) => {
        setCaptchaToken(token);
    };

    const handleCaptchaExpire = () => {
        setCaptchaToken(null);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        updateFormData({ [name]: value });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        // Check if hCaptcha is completed
        if (!captchaToken) {
            setError('Please complete the captcha verification.');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            // Prepare submission data with grade interest prepended to message
            const messageWithGrade = formData.gradeInterest
                ? `Interested Grade: ${formData.gradeInterest}\\n\\n${formData.message || ''}`
                : formData.message || '';

            const submissionData = {
                hcaptchaToken: captchaToken,
                schoolId: formData.school,
                schoolName: formData.schoolName,
                tourDate: formData.tourDate,
                tourTime: formData.tourTime,
                childFirstName: formData.childFirstName,
                childLastName: formData.childLastName,
                childBirthday: formData.birthday,
                parentFirstName: formData.parentFirstName,
                parentLastName: formData.parentLastName,
                parentPhone: formData.phoneNumber,
                parentEmail: formData.email,
                message: messageWithGrade,
                origin: localStorage.getItem('o') ?? '',
                canText: true,
            };

            const response = await fetch(
                `${config.apiBaseUrl}/v1/schedule-tour`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(submissionData),
                }
            );

            if (response.ok) {
                // Scroll to top before navigating
                window.scrollTo({ top: 0, behavior: 'smooth' });
                onNext(); // Move to confirmation step
            } else {
                const errorData = await response.json().catch(() => ({}));
                setError(errorData.message || 'There was an error scheduling your tour. Please try again.');
                // Reset hCaptcha on error
                hcaptchaRef.current?.resetCaptcha();
                setCaptchaToken(null);
            }
        } catch (err) {
            console.error('Tour scheduling error:', err);
            setError('Unable to connect to the server. Please try again later.');
            // Reset hCaptcha on error
            hcaptchaRef.current?.resetCaptcha();
            setCaptchaToken(null);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '600px', mx: 'auto' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    Tell us about yourself
                </Typography>

                {/* Tour Details Summary */}
                <Card
                    sx={{
                        width: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        boxShadow: 3,
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left' }}>
                            {/* School */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <LocationOnIcon sx={{ color: 'primary.main' }} />
                                <Box>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        School
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {formData.schoolName}
                                    </Typography>
                                    {formData.schoolAddress1 && (
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {formData.schoolAddress1}
                                        </Typography>
                                    )}
                                    {formData.schoolAddress2 && (
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {formData.schoolAddress2}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            {/* Date */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <CalendarTodayIcon sx={{ color: 'primary.main' }} />
                                <Box>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Date
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {formData.tourDate ? dayjs(formData.tourDate).format('dddd, MMMM D, YYYY') : 'Not selected'}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Time */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <AccessTimeIcon sx={{ color: 'primary.main' }} />
                                <Box>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Time
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {formData.tourTime || 'Not selected'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <form onSubmit={handleSubmit}>
                    {/* Child First and Last Name */}
                    <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField
                            name="childFirstName"
                            label="Child First Name"
                            value={formData.childFirstName}
                            onChange={handleChange}
                            required
                            fullWidth
                            sx={{ textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                        />
                        <TextField
                            name="childLastName"
                            label="Child Last Name"
                            value={formData.childLastName}
                            onChange={handleChange}
                            required
                            fullWidth
                            sx={{ textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                        />
                    </Box>

                    {/* Birthday */}
                    <DatePicker
                        label="Child Birthday"
                        value={birthdayValue}
                        onChange={(newValue) => {
                            setBirthdayValue(newValue);
                            if (newValue) {
                                updateFormData({ birthday: newValue.format('YYYY-MM-DD') });
                            }
                        }}
                        slotProps={{
                            textField: {
                                required: true,
                                fullWidth: true,
                                margin: 'normal',
                                sx: { mt: 3, textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)' },
                            },
                        }}
                    />

                    {/* Grade Interest */}
                    <FormControl
                        fullWidth
                        sx={{ mt: 2, textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                        disabled={availableGrades.length === 0}
                    >
                        <InputLabel id="grade-interest-label-tour">Grade Interest</InputLabel>
                        <Select
                            labelId="grade-interest-label-tour"
                            name="gradeInterest"
                            value={formData.gradeInterest || ''}
                            onChange={(e) => updateFormData({ gradeInterest: e.target.value as string })}
                            label="Grade Interest"
                        >
                            {availableGrades.map((grade) => (
                                <MenuItem key={grade} value={grade}>
                                    {grade}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Parent First and Last Name */}
                    <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' }, mt: 3 }}>
                        <TextField
                            name="parentFirstName"
                            label="Parent First Name"
                            value={formData.parentFirstName}
                            onChange={handleChange}
                            required
                            fullWidth
                            sx={{ textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                        />
                        <TextField
                            name="parentLastName"
                            label="Parent Last Name"
                            value={formData.parentLastName}
                            onChange={handleChange}
                            required
                            fullWidth
                            sx={{ textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                        />
                    </Box>

                    {/* Parent Phone */}
                    <TextField
                        name="phoneNumber"
                        label="Parent Phone Number"
                        type="tel"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        required
                        fullWidth
                        margin="normal"
                        sx={{ mt: 3, textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                    />

                    {/* Parent Email */}
                    <TextField
                        name="email"
                        label="Parent Email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        fullWidth
                        margin="normal"
                        sx={{ textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                    />

                    {/* Message - Optional */}
                    <TextField
                        name="message"
                        label="Message (Optional)"
                        value={formData.message}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={3}
                        margin="normal"
                        sx={{ textAlign: 'left', backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                    />

                    {/* SMS consent (Twilio A2P 10DLC) */}
                    <SmsConsentCheckbox />

                    {/* hCaptcha */}
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <HCaptcha
                            ref={hcaptchaRef}
                            sitekey={config.hcaptchaSiteKey}
                            onVerify={handleCaptchaVerify}
                            onExpire={handleCaptchaExpire}
                        />
                    </Box>

                    {error && (
                        <Typography sx={{ color: 'red', mt: 2 }}>
                            {error}
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                onBack();
                            }}
                            startIcon={<ArrowBackIcon />}
                            size="large"
                            disabled={submitting}
                        >
                            Back
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={submitting || !captchaToken}
                        >
                            {submitting ? <CircularProgress size={24} /> : 'Schedule Tour'}
                        </Button>
                    </Box>
                </form>
            </Box>
        </LocalizationProvider>
    );
}

export default ContactInfoStep;
